import {
  FrameSink,
  RecordingFormat,
  downloadRecording,
} from '../recorder-core';

/**
 * The region of the source video currently visible in the (aspect-locked)
 * viewport, in source-video pixels. Recomputed every frame so the recording
 * follows the user's zoom and pan as they adjust them mid-take.
 */
export interface CropRect {
  sx: number;
  sy: number;
  sw: number;
  sh: number;
}

interface FlatRecorderDeps {
  getVideoElement: () => HTMLVideoElement | null;
  getOverlayCanvas: () => HTMLCanvasElement | null;
  getCropRect: () => CropRect | null;
}

/**
 * Recorder for the endzone (flat) player.
 *
 * Unlike the panorama, the source here is a plain <video> element rather than
 * a WebGL canvas, so there is no partial-paint tearing to design around: we
 * can drawImage straight from the video every frame. Using the cropping form
 * of drawImage also means we sample the *decoded* video rather than the
 * on-screen presentation, so at 1x zoom a 1080p source fills a 1920x1080
 * output pixel-for-pixel instead of being resampled through screen resolution.
 */
export class FlatRecorder {
  private deps: FlatRecorderDeps;
  private sink = new FrameSink();

  constructor(deps: FlatRecorderDeps) {
    this.deps = deps;
  }

  start(format: RecordingFormat, fps: number): void {
    this.sink.start(format, fps);
  }

  captureFrame(): void {
    this.sink.captureFrame((ctx, width, height) => {
      const video = this.deps.getVideoElement();
      const crop = this.deps.getCropRect();
      // readyState < HAVE_CURRENT_DATA means there is no frame to draw yet;
      // skip rather than emitting a black frame at the head of the file.
      if (!video || !crop || video.readyState < 2) return false;

      try {
        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(video, crop.sx, crop.sy, crop.sw, crop.sh, 0, 0, width, height);
        // The overlay canvas covers exactly the locked viewport box, which is
        // exactly what the crop rect maps to, so it composites 1:1 onto the
        // output with no extra transform.
        const overlay = this.deps.getOverlayCanvas();
        if (overlay && overlay.width > 0 && overlay.height > 0) {
          ctx.drawImage(overlay, 0, 0, width, height);
        }
      } catch { /* composite error; emit whatever the canvas holds */ }
      return true;
    });
  }

  async stop(): Promise<{ blob: Blob; mimeType: string } | null> {
    return this.sink.stop();
  }

  download(blob: Blob, mimeType: string, format: RecordingFormat): void {
    downloadRecording(blob, mimeType, format, 'endzone');
  }

  isRecording(): boolean {
    return this.sink.isRecording();
  }
}
